import '@theme/bonnet-filter';
import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, CartUpdateEvent, VariantSelectedEvent } from '@theme/events';
import { preloadAdjacentSlides, updateVideoPlayback } from '@theme/media-gallery';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import * as haptic from '@theme/haptic';

const QUICK_ADD_SECTION_ID = 'quick-add';
const GALLERY_IMAGE_SELECTOR = '[data-media-gallery-image]';
const SWATCH_SELECTOR = '[data-quick-add-swatch]';

/**
 * Parses quick-add content from raw section HTML.
 * @param {string} html
 * @returns {Element | null}
 */
function parseQuickAddContent(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('[data-quick-add-content]');
}

/**
 * Resolves a product URL (relative or absolute) to an absolute pathname.
 * Used as the canonical cache key for #htmlCache.
 * @param {string} productUrl
 * @returns {string}
 */
function resolveProductUrl(productUrl) {
  return new URL(productUrl, window.location.origin).pathname;
}

/**
 * Fetches quick-add section HTML for a product URL via the section renderer cache.
 * @param {string} productUrl - The product page URL
 * @param {string} [variantId] - Optional variant ID to append
 * @returns {Promise<string>} The raw section HTML
 */
async function fetchQuickAddHTML(productUrl, variantId) {
  const url = new URL(productUrl, window.location.origin);
  if (variantId) url.searchParams.set('variant', variantId);
  return sectionRenderer.getSectionHTML(QUICK_ADD_SECTION_ID, true, url);
}

/**
 * Updates the gallery image manually (only when src differs) and
 * synchronises the new content's img attributes so morph skips the node.
 * @param {Element} container - The live DOM container
 * @param {Element} newContent - The incoming content about to be morphed in
 */
function syncImage(container, newContent) {
  const oldImg = container.querySelector(GALLERY_IMAGE_SELECTOR);
  const newImg = newContent.querySelector(GALLERY_IMAGE_SELECTOR);
  if (!(oldImg instanceof HTMLImageElement) || !(newImg instanceof HTMLImageElement)) return;

  if (oldImg.getAttribute('src') !== newImg.getAttribute('src')) {
    oldImg.setAttribute('src', newImg.getAttribute('src') || '');
    oldImg.setAttribute('srcset', newImg.getAttribute('srcset') || '');
    oldImg.setAttribute('alt', newImg.getAttribute('alt') || '');
    oldImg.setAttribute('width', newImg.getAttribute('width') || '');
    oldImg.setAttribute('height', newImg.getAttribute('height') || '');
    oldImg.hidden = newImg.hidden;
  }

  for (const attr of oldImg.attributes) {
    newImg.setAttribute(attr.name, attr.value);
  }
  newImg.hidden = oldImg.hidden;
}

/**
 * Re-initializes the slider-component inside a container after a morph/content swap.
 * @param {Element} container
 */
function reInitSlider(container) {
  const slider = /** @type {HTMLElement & { reInit?: () => void }} */ (
    container.querySelector('slider-component')
  );
  if (slider?.reInit) slider.reInit();
}

/**
 * Morphed `<script>` tags never run: the size guide trigger's module is imported here, only when the
 * swapped-in content actually carries one (pure size products).
 * @param {Element} container
 */
function loadSizeGuide(container) {
  if (container.querySelector('size-guide-layer')) import('@theme/size-guide-layer');
}

class QuickAddDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  #abortController = new AbortController();

  /** @type {Map<string, string>} productUrl → raw section HTML */
  #htmlCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.#closeOnCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.addEventListener('pointerover', this.#onSwatchHover, { signal });
    this.addEventListener(ThemeEvents.sliderInit, this.#onSliderInit, { signal });
    this.addEventListener(ThemeEvents.sliderSelect, this.#onSliderSelect, { signal });
    this.addEventListener(ThemeEvents.sliderScroll, this.#onSliderScroll, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get contentElement() {
    const content = this.refs.content;
    if (!(content instanceof HTMLElement)) {
      throw new Error('Required ref "content" not found in quick-add-drawer');
    }

    return content;
  }

  setEmptyState() {
    this.contentElement.innerHTML = `<p class="quick-add__empty">${Theme.translations.quick_add_empty}</p>`;
    this.contentElement.setAttribute('aria-busy', 'false');
  }

  /**
   * @returns {boolean}
   */
  get isOpen() {
    const dialog = this.refs.dialog;
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  /**
   * Opens the drawer immediately with a structured skeleton while the payload
   * is fetched. The skeleton markup lives in the drawer shell `<template>`.
   */
  /**
   * Swaps the skeleton into the content area without opening the drawer.
   * Must run synchronously before the payload fetch: a cache-warm fetch can
   * resolve within a microtask, and the skeleton must never overwrite it.
   */
  setSkeletonContent() {
    const template = this.refs.skeleton;
    if (template instanceof HTMLTemplateElement) {
      this.contentElement.replaceChildren(template.content.cloneNode(true));
    } else {
      this.contentElement.replaceChildren();
    }
    this.contentElement.setAttribute('aria-busy', 'true');
  }

  /**
   * Morphs the fetched payload into the already-open drawer (skeleton → content).
   * @param {Element | null} content
   */
  fillContent(content) {
    if (!(content instanceof Element)) {
      this.setEmptyState();
      return;
    }

    this.#htmlCache.clear();
    syncImage(this.contentElement, content);
    morph(this.contentElement, content, { ...MORPH_OPTIONS, childrenOnly: true });
    this.contentElement.setAttribute('aria-busy', 'false');
    reInitSlider(this.contentElement);
    loadSizeGuide(this.contentElement);
  }

  closeDrawer = () => {
    this.closeDialog();
  };

  /**
   * @param {Event} event
   */
  #closeOnCartUpdate = (event) => {
    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) return;
    if (event.detail?.data?.source === 'product-form-component') return;
    this.closeDialog();
  };

  // -- Swatch handlers --

  /**
   * Prefetches quick-add section HTML on swatch hover.
   * Warms both the sectionRenderer cache and the local #htmlCache.
   * Same pattern as product-card.js #handleSwatchHover.
   * @param {PointerEvent} event
   */
  #onSwatchHover = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const link = /** @type {HTMLAnchorElement | null} */ (target?.closest(SWATCH_SELECTOR));
    if (!link || !(link instanceof HTMLAnchorElement)) return;
    if (link.getAttribute('aria-checked') === 'true') return;

    const cacheKey = resolveProductUrl(link.href);
    if (this.#htmlCache.has(cacheKey)) return;

    fetchQuickAddHTML(link.href).then((html) => {
      this.#htmlCache.set(cacheKey, html);
    });
  };

  /**
   * Public handler for declarative `on:click` swatch events.
   * Reads from local cache (pre-warmed on hover) or fetches, then morphs.
   * @param {MouseEvent} event
   */
  handleSwatchClick = async (event) => {
    event.preventDefault();

    const target = /** @type {Element | null} */ (event.target);
    const link = /** @type {HTMLAnchorElement | null} */ (target?.closest(SWATCH_SELECTOR));
    if (!link || link.getAttribute('aria-checked') === 'true') return;

    const productUrl = link.href;
    if (!productUrl) return;

    haptic.light();

    const cacheKey = resolveProductUrl(productUrl);
    const previousOptions = this.#getSelectedOptions();

    // Update swatch active states
    for (const swatch of this.contentElement.querySelectorAll(SWATCH_SELECTOR)) {
      swatch.classList.remove('quick-add__swatch--active');
      swatch.setAttribute('aria-checked', 'false');
      swatch.setAttribute('tabindex', '-1');
    }
    link.classList.add('quick-add__swatch--active');
    link.setAttribute('aria-checked', 'true');
    link.setAttribute('tabindex', '0');
    link.setAttribute('aria-busy', 'true');

    try {
      const html = this.#htmlCache.get(cacheKey)
        || await fetchQuickAddHTML(productUrl);
      this.#htmlCache.set(cacheKey, html);

      const newContent = parseQuickAddContent(html);
      if (!newContent) return;

      this.#applyVariantOptions(newContent, previousOptions);
      syncImage(this.contentElement, newContent);
      morph(this.contentElement, newContent, { ...MORPH_OPTIONS, childrenOnly: true });
      reInitSlider(this.contentElement);
      loadSizeGuide(this.contentElement);
    } catch (error) {
      console.error('Quick add swatch navigation failed:', error);
    }
  };

  /**
   * Reads the currently selected option values from the variant picker.
   * @returns {string[]}
   */
  #getSelectedOptions() {
    const picker = /** @type {(HTMLElement & { selectedValues?: string[] }) | null} */ (
      this.contentElement.querySelector('variant-picker')
    );
    if (!picker || typeof picker.selectedValues === 'undefined') return [];
    return picker.selectedValues;
  }

  /**
   * Patches the parsed content element to pre-select a variant matching the
   * given options before morph. Updates radios, hidden variant ID input, price,
   * and button state — all in the offline DOM so morph renders the final state
   * in one pass with no flash and no fetch.
   * @param {Element} content
   * @param {string[]} options
   */
  #applyVariantOptions(content, options) {
    if (options.length === 0) return;

    const picker = content.querySelector('variant-picker');
    if (!picker) return;

    const json = picker.querySelector('script[type="application/json"]')?.textContent;
    if (!json) return;

    /** @type {Array<{id?: string | number, available?: boolean, options?: string[], price_formatted?: string, compare_at_price_formatted?: string, price?: number, compare_at_price?: number}>} */
    let variants;
    try { variants = JSON.parse(json); } catch { return; }

    const match = variants.find((v) => {
      if (!v.options || !v.available) return false;
      return options.every((val, i) => v.options?.[i] === val);
    });
    if (!match?.id) return;

    // Check the matching radio inputs
    for (let i = 0; i < options.length; i++) {
      for (const radio of picker.querySelectorAll(`input[type="radio"][data-option-index="${i}"]`)) {
        if (!(radio instanceof HTMLInputElement)) continue;
        radio.checked = radio.value === options[i];
      }
    }

    // Update the hidden variant ID input
    const variantInput = content.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(match.id);
    }

    // Update price
    const priceEl = content.querySelector('[data-price]');
    if (priceEl && match.price_formatted) {
      priceEl.textContent = match.price_formatted;
    }

    const compareEl = content.querySelector('[data-compare-price]');
    if (compareEl instanceof HTMLElement) {
      if (match.compare_at_price && match.compare_at_price > (match.price ?? 0)) {
        compareEl.textContent = match.compare_at_price_formatted ?? '';
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    // Update button disabled state
    const button = content.querySelector('[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !match.available;
    }
  }

  /**
   * @param {Event} event
   */
  #onVariantSelected = async (event) => {
    if (!(event instanceof VariantSelectedEvent)) return;

    /** @type {HTMLElement | null} */
    const productForm = this.contentElement.querySelector('product-form-component');
    const productUrl = productForm?.dataset?.productUrl;
    if (!productUrl) return;

    try {
      const resource = /** @type {{id?: string}} */ (event.detail?.resource);
      const html = await fetchQuickAddHTML(productUrl, resource?.id);
      const newContent = parseQuickAddContent(html);
      if (!newContent) return;

      syncImage(this.contentElement, newContent);

      const oldPrice = this.contentElement.querySelector('.quick-add__price');
      const newPrice = newContent.querySelector('.quick-add__price');
      if (oldPrice instanceof HTMLElement && newPrice instanceof HTMLElement) {
        morph(oldPrice, newPrice, { ...MORPH_OPTIONS, childrenOnly: false });
      }
    } catch (error) {
      console.error('Quick add variant update failed', error);
    }
  };

  // -- Slider event handlers --

  /** @param {Event} _event */
  #onSliderInit = (_event) => {
    const slider = /** @type {import('@theme/media-gallery').SliderElement | null} */ (
      this.contentElement.querySelector('slider-component')
    );
    if (!slider) return;
    updateVideoPlayback(slider, 0);
    preloadAdjacentSlides(slider, 0, 1);
    preloadAdjacentSlides(slider, 0, -1);
  };

  /** @param {Event} event */
  #onSliderSelect = (event) => {
    const slider = /** @type {import('@theme/media-gallery').SliderElement | null} */ (
      this.contentElement.querySelector('slider-component')
    );
    if (!slider) return;
    const index = /** @type {CustomEvent} */ (event).detail?.index ?? 0;
    updateVideoPlayback(slider, index);
    preloadAdjacentSlides(slider, index, 1);
    preloadAdjacentSlides(slider, index, -1);
  };

  /** @param {Event} event */
  #onSliderScroll = (event) => {
    const pagination = this.contentElement.querySelector('.media-gallery__pagination');
    if (!pagination || !(pagination instanceof HTMLElement)) return;
    const progress = /** @type {CustomEvent} */ (event).detail?.progress ?? 0;
    pagination.style.setProperty('--slide-progress', String(progress));
  };
}

if (!customElements.get('quick-add-drawer')) {
  customElements.define('quick-add-drawer', QuickAddDrawer);
}

class QuickAddComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    // Warm the section-renderer cache on hover so the skeleton barely flashes.
    this.addEventListener('pointerenter', this.#prefetch, { signal: this.#abortController.signal, once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #prefetch = () => {
    const productUrl = this.dataset.productUrl;
    if (productUrl) fetchQuickAddHTML(productUrl).catch(() => {});
  };

  /**
   * @param {MouseEvent} event
   */
  openDrawer = async (event) => {
    event.preventDefault();

    const drawer = document.getElementById('quick-add-drawer');
    if (!(drawer instanceof QuickAddDrawer)) return;

    const productUrl = this.dataset.productUrl;

    // When launched from within an already-open drawer (e.g. the cart
    // confirmation layer's cross-sell), swap that drawer out for the quick-add
    // drawer instead of stacking two modals.
    const hostDialog = this.closest('dialog');
    const hostComponent = hostDialog?.parentElement;
    const canSwap =
      hostDialog instanceof HTMLDialogElement &&
      hostDialog.open &&
      hostDialog !== drawer.refs.dialog &&
      typeof (/** @type {any} */ (hostComponent)?.closeDialog) === 'function';

    // Content is set synchronously BEFORE the fetch: a prefetched (cache-warm)
    // payload resolves within a microtask, so a skeleton applied later — e.g.
    // inside the async view-transition callback — would overwrite it.
    if (productUrl) {
      drawer.setSkeletonContent();
    } else {
      drawer.setEmptyState();
    }

    if (canSwap && hostDialog instanceof HTMLDialogElement) {
      this.#swapFromHost(hostDialog, /** @type {DialogComponent} */ (hostComponent), drawer, () => {
        if (!drawer.isOpen) drawer.showDialog({ immediate: true });
      });
    } else if (!drawer.isOpen) {
      drawer.showDialog();
    }

    if (!productUrl) return;

    try {
      const html = await fetchQuickAddHTML(productUrl);
      const content = parseQuickAddContent(html);

      if (!(content instanceof Element)) {
        throw new Error('Quick add content not found in section rendering response');
      }

      drawer.fillContent(content);
    } catch (error) {
      console.error(error);
      drawer.setEmptyState();
    }
  };

  /**
   * Swaps an already-open host drawer out for the quick-add drawer using a
   * shared-element view transition, so modals never stack. Mirrors the reverse
   * swap in cart-confirmation.js (quick-add → layer on add). Returning to the
   * host on dismissal is intentionally not handled — closing the quick-add
   * returns to the underlying page.
   * @param {HTMLDialogElement} hostDialog
   * @param {DialogComponent} hostComponent
   * @param {QuickAddDrawer} drawer
   * @param {() => void} openQuickAdd - Opens the (pre-filled) quick-add dialog; content is set by the caller beforehand
   */
  #swapFromHost(hostDialog, hostComponent, drawer, openQuickAdd) {
    const quickAddDialog = drawer.refs.dialog;

    const run = () => {
      hostComponent.closeDialog({ immediate: true });
      openQuickAdd();
    };

    if (!document.startViewTransition) {
      run();
      return;
    }

    hostDialog.style.viewTransitionName = 'drawer-shell';

    const transition = document.startViewTransition(() => {
      hostDialog.style.viewTransitionName = '';
      run();
      if (quickAddDialog instanceof HTMLElement) {
        quickAddDialog.style.viewTransitionName = 'drawer-shell';
      }
    });

    transition.finished.finally(() => {
      if (quickAddDialog instanceof HTMLElement) {
        quickAddDialog.style.viewTransitionName = '';
      }
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
